import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class BffService {
  private readonly apiBase = `${environment.backend.baseUrl}${environment.backend.apiUrl}`;
  private csrfToken: string | null = null;

  constructor(private http: HttpClient) {}

  // Fetches/refreshes the CSRF cookie (XSRF-TOKEN)
  ensureCsrf(): Promise<void> {
    if (environment.authMode !== 'bff') return Promise.resolve();
    return this.http
      .get<{ csrfToken: string }>(`${this.apiBase}/bff/csrf`, { withCredentials: true })
      .toPromise()
      .then((res) => {
        if (res && typeof res.csrfToken === 'string') {
          this.csrfToken = res.csrfToken;
        }
      })
      .catch(() => void 0);
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  // Lightweight identity probe that never returns claims
  whoAmI(): Promise<{ authenticated: boolean; userType: 'customer' | 'employee' | null }>
  {
    return firstValueFrom(
      this.http.get<{ authenticated: boolean; userType: 'customer' | 'employee' | null }>(
        `${this.apiBase}/whoami`,
        { withCredentials: true }
      )
    ).catch(() => ({ authenticated: false, userType: null }));
  }

  signInCustomer(): void {
    window.location.href = `${this.apiBase}/signin/customer`;
  }

  signInEmployee(): void {
    window.location.href = `${this.apiBase}/signin/employee`;
  }

  async signOut(): Promise<void> {
    // For proper OIDC logout, we need to let the browser follow redirects to Azure
    // Use GET instead of POST and redirect the browser, similar to signIn
    // The backend will redirect to Azure's end_session_endpoint, then back to callback, then to frontend

    // Best-effort: clear frontend-accessible cookies/storage first
    try {
      this.clearClientAuthState();
    } catch { /* ignore */ }

    // Redirect browser to /signout GET endpoint - browser will follow the redirect chain:
    // /signout → Azure logout → /signout-callback-* → /login
    window.location.href = `${this.apiBase}/signout`;
  }

  // Simple cookie reader
  private readCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Attempts to remove non-HttpOnly cookies and clear web storage
  private clearClientAuthState(): void {
    // Clear cookies visible to JS
    const cookieStr = document.cookie || '';
    const names = cookieStr.split(';').map(c => c.split('=')[0].trim()).filter(n => !!n);
    const host = location.hostname;
    const parts = host.split('.');
    const domains: string[] = [];
    for (let i = 0; i < parts.length - 1; i++) {
      domains.push(parts.slice(i).join('.'));
    }
    // Always try without domain attribute too
    const expire = 'Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    names.forEach(name => {
      // no-domain
      document.cookie = `${name}=; ${expire}`;
      // with each domain suffix
      domains.forEach(d => {
        document.cookie = `${name}=; ${expire}; domain=${d}`;
        document.cookie = `${name}=; ${expire}; domain=.${d}`;
      });
    });

    // Clear web storage used by app if any
    try { sessionStorage.clear(); } catch { /* ignore */ }
    try { localStorage.clear(); } catch { /* ignore */ }
  }
}
